/**
 * TrialAIExtractionPanel
 * ----------------------
 * Runs AI extraction on the eTMF documents already filed for a trial.
 * - Downloads each PDF/DOCX from the etmf-documents bucket
 * - Extracts text client-side (pdfjs / mammoth)
 * - Calls the extract-trial-metadata edge function (Gemini 2.5 Pro)
 * - Persists results to cro_trials (phase / target_enrollment / protocol_version)
 *   and upserts trial_protocols (inclusion / exclusion / visit_schedule)
 * - Surfaces per-file status and errors LOUDLY (no silent fails).
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractDocumentText } from "@/lib/regulatory/elsa/extractDocumentText";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Sparkles, Loader2, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { extractTrialMetadataInChunks } from "@/lib/trial-site/chunkedTrialExtraction";
import { persistTrialExtraction } from "@/lib/trial-site/persistTrialExtraction";

type ETMFRow = {
  id: string;
  document_name: string;
  document_type: string | null;
  file_url: string;
  created_at: string;
};

type RunState = {
  status: "idle" | "downloading" | "extracting" | "ai" | "saving" | "done" | "failed" | "skipped";
  error?: string;
  summary?: string;
};

const BUCKET = "etmf-documents";

function normalizePhase(p: string | null | undefined): string | null {
  if (!p) return null;
  const m = String(p).match(/([1-4])/);
  return m ? `Phase ${m[1]}` : null;
}

/** Normalize a filename so URL-encoded and plain copies collide. */
function canonicalName(name: string): string {
  let n = name || "";
  try { n = decodeURIComponent(n.replace(/\+/g, " ")); } catch { n = n.replace(/\+/g, " "); }
  return n.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Keep the newest row per canonical filename; return {kept, duplicates}. */
function dedupeDocs(rows: ETMFRow[]): { kept: ETMFRow[]; duplicates: ETMFRow[] } {
  const sorted = [...rows].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const seen = new Map<string, ETMFRow>();
  const duplicates: ETMFRow[] = [];
  for (const r of sorted) {
    const key = canonicalName(r.document_name);
    if (seen.has(key)) duplicates.push(r);
    else seen.set(key, r);
  }
  return { kept: Array.from(seen.values()), duplicates };
}

export function TrialAIExtractionPanel({ trialId }: { trialId: string }) {
  const [docs, setDocs] = useState<ETMFRow[]>([]);
  const [duplicates, setDuplicates] = useState<ETMFRow[]>([]);
  const [runs, setRuns] = useState<Record<string, RunState>>({});
  const [running, setRunning] = useState(false);
  const [purging, setPurging] = useState(false);
  const [protocolExists, setProtocolExists] = useState<boolean | null>(null);

  useEffect(() => {
    void refresh();
  }, [trialId]);

  const refresh = async () => {
    const { data: docsData, error: docsErr } = await supabase
      .from("etmf_documents")
      .select("id, document_name, document_type, file_url, created_at")
      .eq("source_record_id", trialId)
      .order("created_at", { ascending: false });
    if (docsErr) {
      toast({ title: "Could not load eTMF docs", description: docsErr.message, variant: "destructive" });
    }
    const { kept, duplicates: dupes } = dedupeDocs((docsData ?? []) as ETMFRow[]);
    setDocs(kept);
    setDuplicates(dupes);

    const { count } = await supabase
      .from("trial_protocols")
      .select("id", { count: "exact", head: true })
      .eq("trial_id", trialId);
    setProtocolExists((count ?? 0) > 0);
  };

  const purgeDuplicates = async () => {
    if (duplicates.length === 0) return;
    setPurging(true);
    const ids = duplicates.map((d) => d.id);
    const paths = duplicates.map((d) => d.file_url).filter(Boolean);
    // Best-effort storage cleanup — DB delete is the source of truth.
    if (paths.length) await supabase.storage.from(BUCKET).remove(paths).catch(() => {});
    const { error } = await supabase.from("etmf_documents").delete().in("id", ids);
    setPurging(false);
    if (error) {
      toast({ title: "Could not remove duplicates", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: `Removed ${ids.length} duplicate document${ids.length === 1 ? "" : "s"}` });
    await refresh();
  };

  const setRun = (id: string, patch: Partial<RunState>) =>
    setRuns((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { status: "idle" }), ...patch } as RunState }));

  const runOne = async (doc: ETMFRow) => {
    setRun(doc.id, { status: "downloading", error: undefined, summary: undefined });
    try {
      // Signed URL is not required — download() works with the bucket path directly.
      const { data: blob, error: dlErr } = await supabase.storage.from(BUCKET).download(doc.file_url);
      if (dlErr || !blob) throw new Error(dlErr?.message || "Storage download failed");

      setRun(doc.id, { status: "extracting" });
      const file = new File([blob], doc.document_name || "document.pdf", {
        type: blob.type || "application/pdf",
      });
      const { text } = await extractDocumentText(file);
      if (!text || text.length < 200) {
        setRun(doc.id, {
          status: "skipped",
          error:
            text.length < 200
              ? `Only ${text.length} chars of text extracted — likely a scanned PDF. OCR is needed.`
              : "No text extracted from file.",
        });
        return null;
      }

      setRun(doc.id, { status: "ai" });
      const ex = await extractTrialMetadataInChunks(supabase as any, text, doc.document_name, {
        onProgress: ({ label }) => setRun(doc.id, { status: "ai", summary: label }),
      });
      setRun(doc.id, { status: "saving" });

      const saveResult = await persistTrialExtraction(supabase as any, trialId, ex);

      const bits = saveResult.summaryBits;
      setRun(doc.id, { status: "done", summary: bits.join(" · ") || "no new fields" });
      return ex;
    } catch (e: any) {
      setRun(doc.id, { status: "failed", error: e?.message ?? String(e) });
      return null;
    }
  };

  const runAll = async () => {
    if (docs.length === 0) return;
    setRunning(true);
    let ok = 0;
    let fail = 0;
    for (const d of docs) {
      const res = await runOne(d);
      if (res) ok += 1;
      else fail += 1;
    }
    setRunning(false);
    await refresh();
    toast({
      title: `AI extraction complete`,
      description: `${ok} document${ok === 1 ? "" : "s"} extracted, ${fail} skipped/failed.`,
      variant: fail > 0 && ok === 0 ? "destructive" : "default",
    });
  };

  const statusIcon = (s?: RunState["status"]) => {
    switch (s) {
      case "downloading":
      case "extracting":
      case "ai":
      case "saving":
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case "done":
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "skipped":
        return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Protocol Extraction
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Reads filed eTMF documents and fills protocol details, I/E, endpoints, dosing, SoA visits, fee schedule and participant stipends.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={running}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
          <Button size="sm" onClick={runAll} disabled={running || docs.length === 0}>
            {running ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Run AI on {docs.length} doc{docs.length === 1 ? "" : "s"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {protocolExists === false && !running && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs">
            <strong>No protocol intelligence saved yet.</strong> Click "Run AI" to populate visits and inclusion/exclusion
            criteria from the filed eTMF documents.
          </div>
        )}
        {duplicates.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs flex items-start justify-between gap-3">
            <div>
              <strong>{duplicates.length} duplicate upload{duplicates.length === 1 ? "" : "s"} detected.</strong>{" "}
              Same file uploaded twice (once URL-encoded). Older copies are hidden below.
              <ul className="mt-1 list-disc pl-4 text-[11px] text-muted-foreground">
                {duplicates.slice(0, 4).map((d) => (
                  <li key={d.id} className="truncate">{d.document_name}</li>
                ))}
                {duplicates.length > 4 && <li>+ {duplicates.length - 4} more</li>}
              </ul>
            </div>
            <Button size="sm" variant="outline" onClick={purgeDuplicates} disabled={purging}>
              {purging ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Remove duplicates
            </Button>
          </div>
        )}
        {docs.length === 0 && (
          <p className="text-sm text-muted-foreground">No eTMF documents linked to this trial yet.</p>
        )}
        {docs.map((d) => {
          const r = runs[d.id];
          return (
            <div key={d.id} className="rounded border p-2 flex items-start gap-2">
              <div className="mt-1">{statusIcon(r?.status)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{d.document_name}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {d.document_type || "other"}
                  </Badge>
                </div>
                {r?.summary && <p className="text-xs text-emerald-700 mt-0.5">{r.summary}</p>}
                {r?.error && <p className="text-xs text-destructive mt-0.5 break-words">{r.error}</p>}
                {!r && <p className="text-[11px] text-muted-foreground">Ready to extract.</p>}
                {r && r.status !== "done" && r.status !== "failed" && r.status !== "skipped" && !r.error && (
                  <p className="text-[11px] text-muted-foreground capitalize">{r.status}…</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => runOne(d)} disabled={running}>
                Re-run
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default TrialAIExtractionPanel;
